// Witness observations, most-recent-round first. Listing-only records do not
// establish snapshot audit detail; only the complete feed exposes those fields.
//
// "balances_root" lives in the per-snapshot field on the witness envelope in
// the protocol (SPEC §5.4) but the indexer doesn't expose it as a separate
// row yet; when it does, we surface it inline here without touching the API.

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { ageLabel, ageMs, cached } from "../observation";
import { useObservation } from "../observations";

const DEFAULT_SNAPSHOT_FILTERS = { sinceRound: 0, limit: 50, maxLimit: 500 };

export default function SnapshotsPage() {
  const [sinceRound, setSinceRound] = useState(DEFAULT_SNAPSHOT_FILTERS.sinceRound);
  const [limit, setLimit] = useState(DEFAULT_SNAPSHOT_FILTERS.limit);
  const read = useMemo(() => (signal: AbortSignal) => api.witnesses(sinceRound, limit, signal), [sinceRound, limit]);
  const observation = useObservation(read);
  const rows = observation.data?.witnesses;
  const err = observation.error;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-bold text-mesh-accent w-full sm:w-auto sm:flex-1">
          finality snapshots
        </h1>
        <label className="text-sm text-mesh-mute flex items-center gap-2">
          since round
          <input
            type="number"
            min={0}
            value={sinceRound}
            onChange={(e) => setSinceRound(Math.max(0, Number(e.target.value)))}
            className="w-24 px-2 py-1 rounded bg-mesh-panel"
          />
        </label>
        <label className="text-sm text-mesh-mute flex items-center gap-2">
          limit
          <input
            type="number"
            min={1}
            max={DEFAULT_SNAPSHOT_FILTERS.maxLimit}
            value={limit}
            onChange={(e) => setLimit(Math.max(1, Number(e.target.value)))}
            className="w-24 px-2 py-1 rounded bg-mesh-panel"
          />
        </label>
      </div>

      <div className="text-sm text-mesh-mute">
        {rows
          ? `${cached(observation) ? "Cached snapshots" : "Snapshots observed"} ${ageLabel(ageMs(observation))} · ${rows.length} witness record${rows.length === 1 ? "" : "s"}`
          : err ? "Snapshots unavailable — retrying" : "Loading snapshots…"}
        <div className="text-xs">Browser observation age, not mesh finality or health.</div>
      </div>
      {err && (
        <div
          role="alert"
          className="bg-mesh-err/20 text-mesh-err p-3 rounded text-sm"
        >
          {err}
        </div>
      )}

      {!rows && err && (
        <div className="text-mesh-mute text-sm py-6 text-center">No successful snapshot observation yet.</div>
      )}
      {rows?.length === 0 && (
        <div className="text-mesh-mute text-sm py-6 text-center">No snapshots for these filters.</div>
      )}
      {rows?.map((w) => (
        <dl key={w.tx_id} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 p-3 text-sm bg-mesh-panel/40 rounded-lg">
          <div className="col-span-full min-w-0 text-xs">
            <dt className="text-mesh-mute">Witness record</dt>
            <dd><Link to={`/tx/${w.tx_id}`} className="block hex break-all text-mesh-accent hover:underline">{w.tx_id}</Link></dd>
            {w.phys_time == null && <dd className="text-mesh-mute">Witness transaction indexed; snapshot details not indexed.</dd>}
          </div>
          <div className="min-w-0">
            <dt className="text-xs text-mesh-mute">Round</dt>
            <dd>{w.round == null ? "not indexed" : <Link to={`/round/${w.round}`} className="text-mesh-accent hover:underline font-bold">r{w.round}</Link>}</dd>
          </div>
          <div className="min-w-0">
            <dt className="text-xs text-mesh-mute">Phys time</dt>
            <dd className="text-mesh-mute break-words">{w.phys_time == null ? "not indexed" : new Date(w.phys_time * 1000).toLocaleString()}</dd>
          </div>
          {[["Witness", w.witness_pubkey], ["Prev", w.prev_witness], ["VRF", w.vrf_output]].map(([label, value]) => (
            <div key={label} className="min-w-0">
              <dt className="text-xs text-mesh-mute">{label}</dt>
              <dd className="hex break-all" title={value ?? undefined}>{value == null ? "not indexed" : value || "—"}</dd>
            </div>
          ))}
          <div className="min-w-0">
            <dt className="text-xs text-mesh-mute">Finalized</dt>
            <dd>{w.included_tx_ids_json == null ? "not indexed" : (JSON.parse(w.included_tx_ids_json) as string[]).length}</dd>
          </div>
        </dl>
      ))}
    </div>
  );
}
